/**
 * RG: PDP country size-system selector.
 *
 * The size radios render one value span per size system (from the product's
 * size_chart `international` CSV); `data-size-system` on <variant-picker>
 * picks the visible one (CSS in main.css). The server renders the editor
 * default (Size Systems settings via snippets/size-system.liquid); this module
 * persists the shopper's own choice and re-asserts it after variant-picker
 * morphs — Horizon re-renders the whole <variant-picker> from server HTML on
 * every size pick, resetting both the select and the attribute.
 *
 * It also keeps the Size Guide drawer/page US–AU selects in step (their body
 * tables only carry US and AU, so any other system maps to US).
 */
import { StandardEvents } from '@shopify/events';

const STORAGE_KEY = 'rg:size-system';

function readStored() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function store(value) {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Storage unavailable (private mode) — the choice still applies this page.
  }
}

/** @param {Element} picker @param {string} system */
function apply(picker, system) {
  const select = picker.querySelector('[data-size-system-select]');
  if (!(select instanceof HTMLSelectElement)) return;
  // A product's chart may not carry the stored system — leave the default.
  if (!Array.from(select.options).some((option) => option.value === system)) return;
  if (select.value !== system) select.value = system;
  if (picker.getAttribute('data-size-system') !== system) picker.setAttribute('data-size-system', system);
  syncDropdown(select);
}

/**
 * PJ-4893 (#RGR-114): keep the popover's button label + selected option in
 * step with the (hidden) select — after shopper picks AND after
 * variant-picker morphs re-render the whole control.
 * @param {HTMLSelectElement} select
 */
function syncDropdown(select) {
  const wrap = select.closest('[data-size-system-dropdown]');
  if (!wrap) return;
  const label = wrap.querySelector('[data-size-system-label]');
  const current = select.options[select.selectedIndex];
  if (label && current && label.textContent !== current.textContent) label.textContent = current.textContent;
  for (const option of wrap.querySelectorAll('[data-size-system-option]')) {
    if (option.getAttribute('data-value') === select.value) option.setAttribute('aria-current', 'true');
    else option.removeAttribute('aria-current');
  }
}

/** @param {Element | null} [except] */
function closeDropdowns(except) {
  for (const wrap of document.querySelectorAll('[data-size-system-dropdown]')) {
    if (wrap === except) continue;
    const panel = wrap.querySelector('[data-size-system-panel]');
    const toggle = wrap.querySelector('[data-size-system-toggle]');
    if (panel && !panel.hidden) panel.hidden = true;
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }
}

// Popover behaviour (delegated — the control is re-rendered on every
// variant-picker morph, so no per-element listeners).
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const toggle = event.target.closest('[data-size-system-toggle]');
  if (toggle) {
    const wrap = toggle.closest('[data-size-system-dropdown]');
    const panel = wrap?.querySelector('[data-size-system-panel]');
    if (!panel) return;
    closeDropdowns(wrap);
    panel.hidden = !panel.hidden;
    toggle.setAttribute('aria-expanded', panel.hidden ? 'false' : 'true');
    return;
  }
  const option = event.target.closest('[data-size-system-option]');
  if (option) {
    const wrap = option.closest('[data-size-system-dropdown]');
    const select = wrap?.querySelector('[data-size-system-select]');
    const value = option.getAttribute('data-value');
    if (select instanceof HTMLSelectElement && value && select.value !== value) {
      select.value = value;
      // Bubbles into the module's own change handler below (store + applyAll).
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    closeDropdowns();
    return;
  }
  closeDropdowns();
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closeDropdowns();
});

/** @param {string | null} system */
function applyAll(system) {
  if (!system) return;
  for (const picker of document.querySelectorAll('variant-picker[data-size-system]')) {
    apply(picker, system);
  }
  // (The size-guide drawer/page US-AU selects this used to sync are gone —
  // PJ-4920 made those tables market-rendered server-side.)
}

document.addEventListener('change', (event) => {
  if (!(event.target instanceof Element)) return;
  const select = event.target.closest('[data-size-system-select]');
  if (!(select instanceof HTMLSelectElement)) return;
  store(select.value);
  applyAll(select.value);
});

// Variant-picker morphs reset the select to the server default. The morph of a
// same-product size pick only flips checked/selected attributes — no childList
// mutations, and data-size-system isn't rewritten — so a MutationObserver
// filtered to those never fires while morph's syncFormControlState still
// resets the select (bitten 2026-08-28: dropdown reverted to the default on
// every size pick after the first). The ProductSelectEvent's promise resolves
// after the morph — re-assert there.
document.addEventListener(StandardEvents.productSelect, (event) => {
  const promise = /** @type {{ promise?: Promise<unknown> }} */ (event).promise;
  if (!promise || typeof promise.then !== 'function') return;
  promise.then(() => applyAll(readStored())).catch(() => {});
});

// Belt-and-braces for re-renders that DO change the DOM (first pick from the
// no-selection state, injected markup): re-assert on observed mutations too
// (apply() is a no-op when the values already match, so this settles in one
// extra pass).
const observer = new MutationObserver((mutations) => {
  const stored = readStored();
  if (!stored) return;
  for (const mutation of mutations) {
    if (!(mutation.target instanceof Element)) continue;
    const picker = mutation.target.closest('variant-picker[data-size-system]');
    if (picker) apply(picker, stored);
  }
});

for (const picker of document.querySelectorAll('variant-picker[data-size-system]')) {
  observer.observe(picker, {
    attributes: true,
    attributeFilter: ['data-size-system'],
    childList: true,
    subtree: true,
  });
}

applyAll(readStored());
